import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { FormattedMessage } from 'react-intl';
import { Header } from '../../../components/organisms/Header/Header';
import { useConfiguration } from '../../../components/ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { useUrlQuery } from '../../../components/ApplicationProviders/UrlQueryProvider/UrlQueryProvider';
import { DocumentOverlayLink } from '../../../components/screens/molecules/LegalDisclaimer/molecules/DocumentOverlayLink/DocumentOverlayLink';
import { Caption } from '../../../components/atoms/Text/Text';
import { InformationField } from '../../../components/molecules/InformationField/InformationField';
import { H1, H2 } from '../../../components/atoms/Heading/Heading';
import { Button } from '../../../components/atoms/Button/Button';
import { ContentBody } from '../../../components/atoms/ContentBody/ContentBody';
import { Footer } from '../../../components/atoms/Footer/Footer';
import { ScreenWrapper } from '../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { Stack } from '../../../components/atoms/Stack/Stack';
import { messages } from '../../messages';
import { ApplicationUnverifiedLabel } from '../../../components/molecules/ApplicationUnverifiedLabel/ApplicationUnverifiedLabel';
import { PageView } from '../../../models/analytics';
import { usePageViewEvents as usePageViewEventsImport } from '../../../core/Analytics/usePageViewEvents';
import { getLanguageCode } from '../../../utilities/utils';
import { hasHiddenTermsAndScopeMessages, showClientLegalDocs } from '../../../models/configuration';
import { getTermsAndConditionsMarketUrl } from '../../../core/screens/Legal/TermsAndConditions/TermsAndConditionsMarket';
import { getPrivacyPolicyMarketUrl } from '../../../core/screens/Legal/PrivacyPolicy/PrivacyPolicyMarket';
import {
  MerchantConsentClientLegal as MerchantConsentClientLegalImport,
  MerchantConsentClientLegalProps,
} from './MerchantConsentClientLegal';

export type PayMerchantConsentScreenProps = {
  onConsent: () => void;
  isClientVerified: boolean;
  clientName: string;
  usePageViewEvents?: ({ pageView }: { pageView: PageView }) => void;
  MerchantConsentClientLegal?: React.ComponentType<MerchantConsentClientLegalProps>;
};

export const PayMerchantConsentScreen = ({
  onConsent,
  isClientVerified,
  clientName,
  usePageViewEvents = usePageViewEventsImport,
  MerchantConsentClientLegal = MerchantConsentClientLegalImport,
}: PayMerchantConsentScreenProps) => {
  const { locale, clientId, market } = useUrlQuery();
  const languageCode = getLanguageCode(locale);
  const { data: config } = useConfiguration();
  const hideLegal = hasHiddenTermsAndScopeMessages(config);
  const useClientLegalDocs = showClientLegalDocs(config);

  usePageViewEvents({
    pageView: PageView.PAY_MERCHANT_CONSENT_SCREEN,
  });

  return (
    <ScreenWrapper>
      <Header pageView={PageView.PAY_MERCHANT_CONSENT_SCREEN} onBack={undefined} />
      <ContentBody centerVertically centerHorizontally data-testid="pay-merchant-consent-screen">
        <ApplicationUnverifiedLabel isVerified={Boolean(isClientVerified)} />
        <Stack justifyContent="center">
          <Box mx="large" mb="xxsmall">
            <H1 align="center">
              <FormattedMessage {...messages.merchantConsentTitle} />
            </H1>
          </Box>
          <InformationField iconVariant="bank" isDecorated={false} iconColor="primary">
            <Box>
              <H2 mt="xxsmall" mb="xxsmall">
                <FormattedMessage {...messages.merchantConsentPaymentsTitle} />
              </H2>
              <Caption>
                <FormattedMessage {...messages.merchantConsentPaymentsText} />
              </Caption>
            </Box>
          </InformationField>
          {!hideLegal && (
            <InformationField iconVariant="lock" isDecorated={false} iconColor="primary">
              <Box>
                <H2 mt="xxsmall" mb="xxsmall">
                  <FormattedMessage {...messages.merchantConsentTermsTitle} />
                </H2>
                <Caption as="div">
                  {useClientLegalDocs ? (
                    <MerchantConsentClientLegal
                      clientId={clientId}
                      clientName={clientName}
                      locale={locale}
                    />
                  ) : (
                    <FormattedMessage
                      {...messages.merchantConsentTermsText}
                      values={{
                        clientName,
                        termsAndConditions: (
                          <DocumentOverlayLink
                            documentUrl={getTermsAndConditionsMarketUrl({
                              market,
                              language: languageCode,
                            })}
                            pageView={PageView.TERMS_AND_CONDITION_SCREEN}
                          >
                            <FormattedMessage {...messages.merchantConsentTermsLabel} />
                          </DocumentOverlayLink>
                        ),
                        privacyPolicy: (
                          <DocumentOverlayLink
                            documentUrl={getPrivacyPolicyMarketUrl({
                              market,
                              language: languageCode,
                            })}
                            pageView={PageView.PRIVACY_POLICY_SCREEN}
                          >
                            <FormattedMessage {...messages.merchantConsentPolicyLabel} />
                          </DocumentOverlayLink>
                        ),
                      }}
                    />
                  )}
                </Caption>
              </Box>
            </InformationField>
          )}
        </Stack>

        <Footer stickToBottom>
          <Button
            size="large"
            type="submit"
            color="primary"
            variant="contained"
            onClick={onConsent}
          >
            <FormattedMessage {...messages.continue} />
          </Button>
        </Footer>
      </ContentBody>
    </ScreenWrapper>
  );
};
